// Iter 50 — lightweight per-page SEO for the SPA.
// Sets document.title + description + canonical + Open Graph tags on mount.
// On unmount (or when the deps change), restores the sensible defaults from
// public/index.html so the base page tags never end up wrong on other routes.
import { useEffect } from "react";

const DEFAULTS = {
  title: "Rent Lupi · Isola d'Elba — Noleggio scooter e auto dal 1982",
  description:
    "Rent Lupi — Noleggio scooter e auto all'Isola d'Elba. Marciana Marina, dal 1982.",
  image:
    "https://customer-assets.emergentagent.com/job_scooter-app-2/artifacts/enxhmr8p_614F3B80-6D6F-4E54-B856-E95A74B7F73C.png",
  canonical: "https://rentlupielba.it/",
};

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href) {
  if (!href) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * useSeo — per-page meta management.
 *
 * @param {Object} opts
 * @param {string} opts.title        Full document title (Google shows ~60 chars).
 * @param {string} opts.description  Meta description (Google shows ~155 chars).
 * @param {string} [opts.image]      OG/Twitter preview image (absolute URL).
 * @param {string} [opts.canonical]  Canonical URL (absolute). Defaults to
 *                                    https://rentlupielba.it{location.pathname}.
 * @param {Object} [opts.jsonLd]     Optional JSON-LD structured data. Injected as
 *                                    a script tag with id="seo-jsonld-page".
 */
export function useSeo({ title, description, image, canonical, jsonLd }) {
  useEffect(() => {
    const finalTitle = title || DEFAULTS.title;
    const finalDesc = description || DEFAULTS.description;
    const finalImage = image || DEFAULTS.image;
    const finalCanonical =
      canonical ||
      (typeof window !== "undefined"
        ? `https://rentlupielba.it${window.location.pathname}`
        : DEFAULTS.canonical);

    document.title = finalTitle;
    upsertMeta("name", "description", finalDesc);
    upsertMeta("property", "og:title", finalTitle);
    upsertMeta("property", "og:description", finalDesc);
    upsertMeta("property", "og:image", finalImage);
    upsertMeta("property", "og:image:secure_url", finalImage);
    upsertMeta("property", "og:url", finalCanonical);
    upsertMeta("name", "twitter:title", finalTitle);
    upsertMeta("name", "twitter:description", finalDesc);
    upsertMeta("name", "twitter:image", finalImage);
    upsertCanonical(finalCanonical);

    let scriptEl = null;
    if (jsonLd) {
      const existing = document.getElementById("seo-jsonld-page");
      if (existing) existing.remove();
      scriptEl = document.createElement("script");
      scriptEl.type = "application/ld+json";
      scriptEl.id = "seo-jsonld-page";
      scriptEl.text = JSON.stringify(jsonLd);
      document.head.appendChild(scriptEl);
    }

    return () => {
      // Restore defaults so subsequent routes without useSeo don't inherit
      // stale meta from the previous page.
      document.title = DEFAULTS.title;
      upsertMeta("name", "description", DEFAULTS.description);
      upsertMeta("property", "og:title", DEFAULTS.title);
      upsertMeta("property", "og:description", DEFAULTS.description);
      upsertMeta("property", "og:image", DEFAULTS.image);
      upsertMeta("property", "og:image:secure_url", DEFAULTS.image);
      upsertMeta("property", "og:url", DEFAULTS.canonical);
      upsertMeta("name", "twitter:title", DEFAULTS.title);
      upsertMeta("name", "twitter:description", DEFAULTS.description);
      upsertMeta("name", "twitter:image", DEFAULTS.image);
      upsertCanonical(DEFAULTS.canonical);
      const stale = document.getElementById("seo-jsonld-page");
      if (stale) stale.remove();
    };
  }, [title, description, image, canonical, jsonLd]);
}
